import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { catchError, filter, map, of, startWith, switchMap } from 'rxjs';
import { PersonSelectComponent } from '../person-select/person-select.component';
import { CreateDefectRequest, Defect, DefectSeverity } from '../../models/defect.model';
import { DefectService } from '../../services/defect.service';
import { ProjectMemberService } from '../../services/project-member.service';
import { reviewerOptionsFromMembers } from '../../shared/project-member-options';
import { FIELD_LENGTHS } from '../../shared/field-lengths';

const SEVERITIES: readonly DefectSeverity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const ERROR_KEY_BY_STATUS: Readonly<Record<number, string>> = {
  400: 'defect.capture.error.invalid',
  403: 'defect.capture.error.forbidden',
  404: 'defect.capture.error.notFound',
  409: 'defect.capture.error.stepNotFailed',
};

/**
 * Vorlaeufige Gestaltung ohne Design-Entwurf (NEX-1503) auf `lib-modal`; der Angleich ans
 * Design folgt mit NEX-1690. Offen ist der Dialog, solange {@link stepResultId} gesetzt ist.
 */
@Component({
  selector: 'app-defect-erfassen-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatFormField,
    MatHint,
    MatInput,
    MatLabel,
    Modal,
    PersonSelectComponent,
    TranslatePipe,
  ],
  templateUrl: './defect-erfassen-dialog.component.html',
  styleUrl: './defect-erfassen-dialog.component.scss',
})
export class DefectErfassenDialogComponent {
  private readonly defectService = inject(DefectService);
  private readonly memberService = inject(ProjectMemberService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly stepResultId = input<string | null>(null);

  readonly created = output<Defect>();
  readonly closed = output<void>();

  readonly fieldLengths = FIELD_LENGTHS;
  readonly severities = SEVERITIES;

  readonly form = new FormGroup({
    title: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)],
    }),
    severity: new FormControl<DefectSeverity>('MEDIUM', { nonNullable: true, validators: Validators.required }),
    description: new FormControl('', {
      nonNullable: true,
      validators: Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX),
    }),
    assigneeUserId: new FormControl<string | null>(null),
  });

  readonly open = computed(() => !!this.stepResultId());

  /** Erst beim Oeffnen geladen: die Komponente steht dauerhaft in Seiten, die sie selten brauchen. */
  readonly personOptions = toSignal(
    toObservable(this.stepResultId).pipe(
      filter((id): id is string => !!id),
      switchMap(() =>
        this.memberService.getMembers(this.projectId()).pipe(
          map(reviewerOptionsFromMembers),
          catchError(() => of([])),
        ),
      ),
    ),
    { initialValue: [] },
  );

  readonly submitting = signal(false);
  readonly errorKey = signal<string | null>(null);

  private readonly formValue = toSignal(this.form.valueChanges.pipe(startWith(this.form.getRawValue())), {
    requireSync: true,
  });

  readonly descriptionLength = computed(() => this.formValue().description?.length ?? 0);

  readonly canSubmit = computed(() => !this.submitting() && !!this.formValue().title?.trim() && this.form.valid);

  constructor() {
    effect(() => {
      if (!this.stepResultId()) return;
      this.form.reset();
      this.errorKey.set(null);
      this.submitting.set(false);
    });
  }

  submit(): void {
    const stepResultId = this.stepResultId();
    if (!stepResultId || !this.canSubmit()) return;
    this.submitting.set(true);
    this.errorKey.set(null);
    this.defectService
      .create(this.projectId(), stepResultId, this.request())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (defect) => {
          this.submitting.set(false);
          this.created.emit(defect);
        },
        error: (error: HttpErrorResponse) => {
          this.submitting.set(false);
          this.errorKey.set(ERROR_KEY_BY_STATUS[error.status] ?? 'defect.capture.error.generic');
        },
      });
  }

  cancel(): void {
    this.closed.emit();
  }

  private request(): CreateDefectRequest {
    const value = this.form.getRawValue();
    const description = value.description.trim();
    return {
      title: value.title.trim(),
      severity: value.severity,
      ...(description ? { description } : {}),
      ...(value.assigneeUserId ? { assigneeUserId: value.assigneeUserId } : {}),
    };
  }
}
